import { Component, Input, OnInit } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';

interface VariantColorGroup {
  color: string;
  items: any[];
  sizes: string[];
  expanded: boolean;
}

@Component({
  selector: 'app-variant-selector',
  templateUrl: './variant-selector.component.html',
  styleUrls: ['./variant-selector.component.less'],
})
export class VariantSelectorComponent implements OnInit {
  @Input() jsonData: string = '';
  @Input() onQuantityChanged:
    | ((
        variant: any,
        newQuantity: number,
        fromPlusMinusButtons?: boolean,
        fromVariant?: boolean,
      ) => Promise<void> | void)
    | undefined;

  items: any[] = [];
  colorGroups: VariantColorGroup[] = [];

  constructor(private drawerRef: NzDrawerRef) {}

  ngOnInit(): void {
    this.parseJson();
    this.buildViewModel();
  }

  parseJson(): void {
    if (!this.jsonData) {
      this.items = [];
      return;
    }

    try {
      this.items = JSON.parse(this.jsonData).map((x: any) => ({
        ...x,
        SelectedQuantity: Number(x.SelectedQuantity) || 0,
        LineNum: Number(x.LineNum) || -1,
      }));
    } catch (e) {
      console.error('Invalid JSON', e);
      this.items = [];
    }
  }

  buildViewModel(): void {
    const grouped: Record<string, any[]> = {};

    for (const item of this.items) {
      const color = item.Color || 'No Color';
      if (!grouped[color]) {
        grouped[color] = [];
      }
      grouped[color].push(item);
    }

    this.colorGroups = Object.keys(grouped).map((color) => {
      const items = grouped[color];
      const sizes = this.sortSizes(
        items.map((x) => x.Size || '').filter((x) => x !== ''),
      );

      return {
        color,
        items,
        sizes,
        expanded: true,
      };
    });
  }

  sortSizes(sizes: string[]): string[] {
    const preferredOrder = [
      'XXS',
      'XS',
      'S',
      'M',
      'L',
      'XL',
      '2XL',
      '3XL',
      '4XL',
      '5XL',
      '6XL',
      '7XL',
    ];

    return [...sizes].sort((a, b) => {
      const ai = preferredOrder.indexOf((a || '').toUpperCase());
      const bi = preferredOrder.indexOf((b || '').toUpperCase());

      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.localeCompare(b);
    });
  }

  toggleGroup(group: VariantColorGroup): void {
    group.expanded = !group.expanded;
  }

  getItem(group: VariantColorGroup, size: string): any | undefined {
    return group.items.find((x) => (x.Size || '') === size);
  }

  async onQtyInput(item: any | undefined, value: any): Promise<void> {
    if (!item) {
      return;
    }

    const qty = Number(value);
    item.SelectedQuantity = isNaN(qty) || qty < 0 ? 0 : qty;

    if (this.onQuantityChanged) {
      await this.onQuantityChanged(item, item.SelectedQuantity, false, true);
    }
  }

  totalQuantity(): number {
    return this.items.reduce(
      (sum, x) => sum + Number(x.SelectedQuantity || 0),
      0,
    );
  }

  ok(): void {
    this.drawerRef.close(this.items);
  }
}
